import {isNullOrEmpty} from "@emburse/ui-core";
import {FieldError} from "react-hook-form";
import {ApprovalState, BillState, PostingDate} from '../enums';

export const textFieldPattern = /\W/;
export const textFieldPatternWithDashAndDot = /[^\w.-]/;
export const digitPattern = /\D/;
export const lodash = require("lodash");

export const getCustomFieldsTimeOut = (): number => {
    return Number(process.env.REACT_APP_CUSTOM_FIELDS_SYNC_TIMEOUT ?? 30);
};

export const delayCustomFields = async (): Promise<void> => {
    await new Promise(resolve => setTimeout(resolve, getCustomFieldsTimeOut() * 1000));
};

export const getHelperText = (helperText: string, requiredMessageError: string, validationMessageError: string, field?: FieldError) => {
    let helperTextOrErrorMessage = helperText;
    if(field !== undefined) {
        helperTextOrErrorMessage = (field.type === 'required') ? requiredMessageError : validationMessageError;
    }
    return helperTextOrErrorMessage;
}

export const isEmptyObject = (obj: Record<string, unknown>) => {
    return (Object.keys(obj).length === 0 && obj.constructor === Object);
};

export const toBoolean = (prop: unknown, fallback = false) => {
    let boolValue = fallback;
    if(typeof prop === 'boolean') {
        boolValue = prop;
    } else if(typeof prop === 'string') {
        boolValue = (prop === 'true');
    }
    return boolValue;
};

export const formatBytes = (bytes: number, decimals = 2) => {
    if(bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + sizes[i];
};

export const scrollToRef = (ref: { current: { scrollIntoView: (options?: ScrollIntoViewOptions) => void } | null }) => {
    return ref.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
};

export const capitalize = (value: string) => {
    if(value) {
        return value.charAt(0).toUpperCase() + value.slice(1);
    }
    return value;
};

export const toNumber = (prop: unknown, fallback = 0) => {
    let numValue = fallback;
    if(typeof prop === 'string') {
        numValue = Number(prop);
    }
    return isNaN(numValue) ? fallback : numValue;
};

export const excludeStrFromArrayOfStrs = (arr: string[], str: string) => {
    return arr.filter((val) => {
        return val !== str;
    });
};

export const getDefaultValue = <T>(value: T) => {
    return isNullOrEmpty(value) ? undefined : value;
};

export const getValueByKey = <T extends object>(list: T, value: string | undefined | null) => {
    if(value !== null && value !== undefined) {
        return Object.entries(list).find(([key]) => key === value)?.[1] as string;
    } else {
        return '';
    }
};

export const ERROR_504 = "504";

export const getNullDefaultValueIfEmpty = (value: string) => {
    return (value.trim() === '' ? null : value) as string;
};

export const isValuesExistsKeyMap = <T>(values: string[] | undefined, list: Map<string, T>): boolean => {
    if (values === undefined) {
        return false;
    }
    return values.some((element: string) => !list.has(element));
};

export const joinMandatoryFieldNames = (fields: string[]): string => {
    return fields.reduce((acc, element, index) => {
        if (index === 0) return acc.concat(element);
        if (index < fields.length - 1) return acc.concat(', ' + element);
        return acc.concat(' and ' + element);
    }, "");
};

export const getCustomFieldsMap = <T extends { internalId: string }>(fields: T[]): Map<string, T> => {
    const map = new Map<string, T>();
    fields.forEach(value => map.set(value.internalId, value));
    return map;
};

export const getValidBillState = (value: string | undefined): BillState => {
    return Object.values(BillState).includes(value as BillState) ? value as BillState : BillState.Submit;
};

export const getValidApprovalState = (value: string | undefined): ApprovalState => {
    return Object.values(ApprovalState).includes(value as ApprovalState) ? value as ApprovalState : ApprovalState.Submitted;
};

export const getValidPostingDate = (value: string | undefined): keyof typeof PostingDate => {
    const keys = Object.keys(PostingDate) as Array<keyof typeof PostingDate>;
    return keys.includes(value as keyof typeof PostingDate) ? value as keyof typeof PostingDate : keys[0];
};

export const getMapUserSupervisorDefaultValue = (value: string | undefined): string => {
    return lodash.isEmpty(value) ? 'supervisor' : value as string;
};

export const getAccountsMap = (accounts: { id: string; name: string }[]): Map<string, string> => {
    const map = new Map<string, string>();
    accounts.forEach(value => map.set(value.id, value.name + " (" + value.id + ")"));
    return map;
};

export const isValueExistsKeyMap = (value: string | undefined, list: Map<string, string>): boolean => {
    if (!value) return false;
    return list.has(value);
};